// Documentation API functions — endpoint list matches QREDITCHARGE_API.md

import type { DocumentationConfig } from './documentation.schema';

// Static documentation data built from real registered routes
const staticDocumentation: DocumentationConfig = {
  title: 'QreditCharge — Credit Orchestration API',
  description:
    'API reference for integrating with QreditCharge. All endpoints except health checks require an X-API-Key header. Auth is API-key only — there are no session tokens or OAuth flows.',
  sections: [
    {
      title: 'Health',
      description:
        'Liveness, readiness, and diagnostic probes. No authentication required. Use these to monitor service status and database connectivity.',
      endpoints: [
        {
          method: 'GET',
          path: '/health',
          description: 'Basic liveness check. Returns { status: "ok" } when the server is running.',
          example: 'curl /health',
        },
        {
          method: 'GET',
          path: '/health/detailed',
          description: 'Detailed system status including memory, uptime, and dependency states.',
          example: 'curl /health/detailed',
        },
        {
          method: 'GET',
          path: '/health/database',
          description: 'Database connectivity check. Returns connection state and latency.',
          example: 'curl /health/database',
        },
        {
          method: 'GET',
          path: '/health/readiness',
          description: 'Readiness probe — indicates whether the service is ready to accept traffic.',
          example: 'curl /health/readiness',
        },
        {
          method: 'GET',
          path: '/health/liveness',
          description: 'Liveness probe for orchestrators (Kubernetes, Render health checks, etc.).',
          example: 'curl /health/liveness',
        },
      ],
    },
    {
      title: 'API Keys',
      description:
        'Manage API keys for your account. Keys are used in the X-API-Key header on every authenticated request.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/keys',
          description: 'List all API keys associated with the authenticated account.',
          example: "curl /api/keys -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/api/keys',
          description: 'Generate a new API key. Returns the key value once — store it immediately.',
          example: "curl -X POST /api/keys -H 'X-API-Key: your-key' -d '{\"label\":\"production\"}'",
        },
        {
          method: 'DELETE',
          path: '/api/keys/:id',
          description: 'Revoke an API key by ID. The key becomes invalid immediately.',
          example: "curl -X DELETE /api/keys/key_123 -H 'X-API-Key: your-key'",
        },
      ],
    },
    {
      title: 'Credits',
      description:
        'Core credit ledger operations. Allocate qredits after a successful purchase, consume them when features are used, and retrieve live balances.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/credits/:userId',
          description: 'Return the current qredit balance and expiry schedule for a user.',
          example: "curl /api/credits/user_123 -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/api/credits/allocate',
          description:
            'Allocate qredits to a user. Called internally after a payment webhook confirms settlement. Enforces the 1 999-qredit account cap.',
          example:
            "curl -X POST /api/credits/allocate -H 'X-API-Key: your-key' -d '{\"userId\":\"user_123\",\"amount\":100}'",
        },
        {
          method: 'POST',
          path: '/api/credits/consume',
          description:
            'Deduct qredits from a user balance. Idempotent — pass a unique operationId to prevent double-deduction.',
          example:
            "curl -X POST /api/credits/consume -H 'X-API-Key: your-key' -d '{\"userId\":\"user_123\",\"amount\":10,\"operationId\":\"op_abc\"}'",
        },
      ],
    },
    {
      title: 'Payments',
      description:
        'One-time qredit purchase flows. Fetch a Braintree client token first, then submit a payment nonce with the desired qredit quantity. Credits are allocated when the settlement webhook arrives.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/payments/client-token',
          description:
            'Generate a Braintree client token for initialising the Drop-in UI or hosted fields on the frontend.',
          example: "curl /api/payments/client-token -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/api/payments/create',
          description:
            'Create a one-time payment transaction. Accepts a Braintree payment nonce and a qredit quantity (1–1 999). Daily purchase limit: 1 999 qredits.',
          example:
            "curl -X POST /api/payments/create -H 'X-API-Key: your-key' -d '{\"userId\":\"user_123\",\"nonce\":\"...\",\"amount\":50}'",
        },
        {
          method: 'GET',
          path: '/api/payments/subscription/billing/:userId',
          description:
            'Stream billing history as newline-delimited JSON (NDJSON). Each line is one billing event; the final line is a pagination cursor object.',
          example:
            "curl '/api/payments/subscription/billing/user_123?limit=20' -H 'X-API-Key: your-key'",
        },
      ],
    },
    {
      title: 'Subscriptions',
      description:
        'Recurring monthly qredit plans. Lifecycle events (renewal, cancellation, past-due) are handled automatically via webhooks — qredit balances update without any manual call.',
      endpoints: [
        {
          method: 'POST',
          path: '/api/subscriptions/create',
          description: 'Create a new recurring subscription for a user.',
          example:
            "curl -X POST /api/subscriptions/create -H 'X-API-Key: your-key' -d '{\"userId\":\"user_123\",\"planId\":\"qredit_50_monthly\",\"nonce\":\"...\"}'",
        },
        {
          method: 'GET',
          path: '/api/subscriptions/:userId',
          description: "Retrieve the user's active subscription details.",
          example: "curl /api/subscriptions/user_123 -H 'X-API-Key: your-key'",
        },
        {
          method: 'PUT',
          path: '/api/subscriptions/:userId/plan',
          description: 'Upgrade or downgrade the subscription plan.',
          example:
            "curl -X PUT /api/subscriptions/user_123/plan -H 'X-API-Key: your-key' -d '{\"planId\":\"qredit_100_monthly\"}'",
        },
        {
          method: 'POST',
          path: '/api/subscriptions/:userId/cancel',
          description: 'Cancel a subscription at the end of the current billing period.',
          example: "curl -X POST /api/subscriptions/user_123/cancel -H 'X-API-Key: your-key'",
        },
        {
          method: 'DELETE',
          path: '/api/subscriptions/:userId',
          description: 'Immediately delete a subscription record. Use cancel for graceful end-of-period cancellation.',
          example: "curl -X DELETE /api/subscriptions/user_123 -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/api/subscriptions/:userId/credit-pack',
          description: 'Add a one-time credit pack to an existing subscription.',
          example:
            "curl -X POST /api/subscriptions/user_123/credit-pack -H 'X-API-Key: your-key' -d '{\"packId\":\"pack_50\"}'",
        },
        {
          method: 'PUT',
          path: '/api/subscriptions/:userId/credit-pack',
          description: 'Replace the credit pack associated with an existing subscription.',
          example:
            "curl -X PUT /api/subscriptions/user_123/credit-pack -H 'X-API-Key: your-key' -d '{\"packId\":\"pack_100\"}'",
        },
        {
          method: 'POST',
          path: '/api/subscriptions/:userId/auto-renew',
          description: 'Toggle automatic renewal on or off.',
          example:
            "curl -X POST /api/subscriptions/user_123/auto-renew -H 'X-API-Key: your-key' -d '{\"enabled\":true}'",
        },
      ],
    },
    {
      title: 'Webhooks',
      description:
        'Incoming payment provider webhooks are queued, normalised, and processed automatically. The status endpoint lets satellite apps poll for event outcomes; the admin endpoints enable inspection and replay of failed events.',
      endpoints: [
        {
          method: 'GET',
          path: '/webhooks/status/:eventId',
          description: 'Poll the processing status of a specific webhook event by its ID.',
          example: "curl /webhooks/status/evt_123 -H 'X-API-Key: your-key'",
        },
        {
          method: 'GET',
          path: '/webhooks/admin/events',
          description:
            'List payment events. Filter by provider, status, or processed flag. Supports page and limit query params.',
          example:
            "curl '/webhooks/admin/events?provider=creem&processed=false' -H 'X-API-Key: your-key'",
        },
        {
          method: 'GET',
          path: '/webhooks/admin/events/:id',
          description: 'Retrieve a single payment event including its full retryHistory audit trail.',
          example: "curl /webhooks/admin/events/evt_123 -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/webhooks/admin/events/:eventId/retry',
          description:
            'Replay a failed event through the normaliser and dispatcher pipeline. Appends a retryHistory entry. Already-processed events are skipped.',
          example: "curl -X POST /webhooks/admin/events/evt_123/retry -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/webhooks/admin/events/retry-bulk',
          description:
            'Retry up to 100 failed events in one request. Returns per-id outcomes (retried / failed / skipped_processed / not_found) and an aggregate summary.',
          example:
            "curl -X POST /webhooks/admin/events/retry-bulk -H 'X-API-Key: your-key' -d '{\"ids\":[\"evt_1\",\"evt_2\"]}'",
        },
      ],
    },
    {
      title: 'Analytics',
      description:
        'Aggregate reporting endpoints for payment, subscription, and webhook metrics. All analytics endpoints require API key auth.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/analytics/kpis',
          description: 'Return top-level KPIs: total revenue, active subscriptions, credit allocation totals, and churn rate.',
          example: "curl /api/analytics/kpis -H 'X-API-Key: your-key'",
        },
        {
          method: 'GET',
          path: '/api/analytics/payments',
          description: 'Payment volume and success-rate metrics for a date range.',
          example:
            "curl '/api/analytics/payments?startDate=2026-01-01&endDate=2026-02-01' -H 'X-API-Key: your-key'",
        },
        {
          method: 'GET',
          path: '/api/analytics/subscriptions',
          description: 'Subscription counts, plan distribution, and renewal metrics.',
          example: "curl /api/analytics/subscriptions -H 'X-API-Key: your-key'",
        },
        {
          method: 'GET',
          path: '/api/analytics/webhooks',
          description: 'Webhook event volume, processing success rate, and retry counts for a date range.',
          example:
            "curl '/api/analytics/webhooks?startDate=2026-01-01&endDate=2026-02-01' -H 'X-API-Key: your-key'",
        },
        {
          method: 'POST',
          path: '/api/analytics/custom',
          description: 'Run a custom analytics query with a user-defined metric set and date range.',
          example:
            "curl -X POST /api/analytics/custom -H 'X-API-Key: your-key' -d '{\"metrics\":[\"revenue\"],\"startDate\":\"2026-01-01\",\"endDate\":\"2026-02-01\"}'",
        },
      ],
    },
    {
      title: 'User Payment Provider',
      description:
        'Override which payment provider (Braintree or Creem) a specific user is routed to. Defaults to DEFAULT_PAYMENT_PROVIDER when no override is set.',
      endpoints: [
        {
          method: 'GET',
          path: '/api/users/:id/payment-mode',
          description:
            'Return the current payment provider override for a user. paymentMode is null when using the system default.',
          example: "curl /api/users/user_123/payment-mode -H 'X-API-Key: your-key'",
        },
        {
          method: 'PATCH',
          path: '/api/users/:id/payment-mode',
          description:
            'Switch a user to a specific provider. Writes an audit record capturing previous mode, new mode, and the calling API key.',
          example:
            "curl -X PATCH /api/users/user_123/payment-mode -H 'X-API-Key: your-key' -d '{\"paymentMode\":\"creem\",\"reason\":\"user preference\"}'",
        },
      ],
    },
  ],
};

// Fetch API documentation - returns static data
export async function fetchDocumentation(): Promise<DocumentationConfig> {
  return staticDocumentation;
}

// Generate API documentation - stub function
export async function generateDocumentation(): Promise<{ success: boolean }> {
  return { success: true };
}
